import { uploadSessionRecording } from "./blobStorage";
import { SessionRecording, SessionState } from "../types/SessionState";
import { RecordingErrorHandler, RecordingError } from "./errorHandling";
import fixWebmDuration from "fix-webm-duration";
export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private startTime: number = 0;
  private sessionId: string;
  private onStatusChange: (status: string) => void;
  private recordingId: string | null = null;
  private transcriptionState: SessionState;
  private errorHandler: RecordingErrorHandler;
  private recoveryInterval: number | null = null;
  private mimeType: string = "audio/webm";
  private readonly debug: boolean =
    (import.meta as any)?.env?.VITE_DEBUG_RECORDING === "1" ||
    (import.meta as any)?.env?.DEV === true;

  constructor(
    sessionId: string,
    onStatusChange: (status: string) => void,
    initialState: SessionState = SessionState.Idle
  ) {
    this.sessionId = sessionId;
    this.onStatusChange = onStatusChange;
    this.transcriptionState = initialState;
    this.errorHandler = new RecordingErrorHandler();

    // Check for recoverable recording on init
    this.checkForRecovery();

    // Set up recovery data saving
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  }

  private async checkForRecovery() {
    const recoveryData = await RecordingErrorHandler.checkForRecovery();
    if (recoveryData && recoveryData.sessionId === this.sessionId) {
      try {
        // Upload recovered recording
        const url = await uploadSessionRecording(
          this.sessionId,
          recoveryData.blob
        );
        if (url) {
          // Create recording metadata in database
          const response = await fetch(
            `/api/sessions/${this.sessionId}/recordings`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                blobUrl: url,
                startTime: new Date(recoveryData.startTime).toISOString(),
                durationSeconds: Math.round(
                  (Date.now() - recoveryData.startTime) / 1000
                ),
                status: "completed",
                fileSizeBytes: recoveryData.blob.size,
                mimeType: recoveryData.blob.type,
                metadata: {
                  recovered: true,
                  originalMetadata: recoveryData.metadata,
                },
              }),
            }
          );

          if (response.ok) {
            RecordingErrorHandler.clearRecoveryData();
            this.onStatusChange("completed");
          }
        }
      } catch (error) {
        console.error("Failed to recover recording:", error);
      }
    }
  }

  private handleBeforeUnload = () => {
    if (this.mediaRecorder?.state === "recording") {
      RecordingErrorHandler.saveRecoveryData(
        this.sessionId,
        this.chunks,
        this.startTime,
        { transcriptionState: this.transcriptionState }
      );
    }
  };

  private pickSupportedMimeType(): string {
    const preferred = [
      "audio/webm;codecs=opus",
      "audio/webm",
    ];

    for (const t of preferred) {
      try {
        if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t)) {
          return t;
        }
      } catch {
        // ignore and try next
      }
    }

    // Fall back to browser default if none are explicitly supported
    return "audio/webm";
  }

  private async logBlobSample(label: string, blob: Blob): Promise<void> {
    if (!this.debug) return;
    try {
      const head = new Uint8Array(await blob.slice(0, 64).arrayBuffer());
      const hex = Array.from(head)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join(" ");
      console.log(`[recording] ${label}`, {
        type: blob.type,
        size: blob.size,
        head64: hex,
      });
    } catch (e) {
      console.warn(`[recording] failed to log ${label} sample`, e);
    }
  }

  async start(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Ensure fresh state (important if start is called multiple times)
      this.chunks = [];
      this.startTime = Date.now();
      this.mimeType = this.pickSupportedMimeType();

      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: this.mimeType,
      });

      if (this.debug) {
        console.log("[recording] MediaRecorder created", {
          mimeTypeRequested: this.mimeType,
          mimeTypeActual: (this.mediaRecorder as any).mimeType,
          audioTracks: stream.getAudioTracks().length,
        });
      }

      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          this.chunks.push(e.data);
          if (this.debug && this.chunks.length === 1) {
            // Only log the first chunk to avoid spamming.
            this.logBlobSample("first chunk", e.data);
          }
          // Save recovery data periodically
          RecordingErrorHandler.saveRecoveryData(
            this.sessionId,
            this.chunks,
            this.startTime,
            { transcriptionState: this.transcriptionState }
          );
        }
      };

      this.mediaRecorder.onstop = async () => {
        const durationMs = Date.now() - this.startTime;
        const durationSeconds = durationMs / 1000;

        const rawBlob = new Blob(this.chunks, { type: this.mimeType });
        await this.logBlobSample("raw blob", rawBlob);

        // Some browsers still produce WebM without a usable duration; patching the container metadata
        // avoids the ubiquitous "0:00" playback issue.
        let audioBlob: Blob = rawBlob;
        try {
          audioBlob = await fixWebmDuration(rawBlob, durationMs);
          await this.logBlobSample("fixed blob", audioBlob);
        } catch (e) {
          console.warn("Failed to fix WebM duration metadata; uploading raw blob", e);
        }

        try {
          const url = await uploadSessionRecording(this.sessionId, audioBlob, {
            startTime: new Date(this.startTime).toISOString(),
            durationSeconds: Math.round(durationSeconds),
            status: "completed",
            fileSizeBytes: audioBlob.size,
            mimeType: audioBlob.type,
            metadata: {
              transcriptionState: this.transcriptionState,
            },
          });
          if (url) {
            this.recordingId = url;
            this.onStatusChange("completed");
            RecordingErrorHandler.clearRecoveryData();
          }
        } catch (error) {
          const recordingError: RecordingError = {
            type:
              error instanceof Error && error.message.includes("upload")
                ? "storage"
                : "database",
            message: error instanceof Error ? error.message : String(error),
            retryable: true,
            timestamp: new Date(),
            context: { durationSeconds, chunks: this.chunks.length },
          };

          const shouldRetry = await this.errorHandler.handleError(
            recordingError
          );
          if (shouldRetry && this.mediaRecorder) {
            // Store the current onstop handler
            const currentOnStop = this.mediaRecorder.onstop;
            if (currentOnStop) {
              // Call the handler directly with proper binding
              currentOnStop.bind(this.mediaRecorder)(new Event("stop"));
            }
          } else {
            this.onStatusChange("failed");
          }
        }
      };

      this.mediaRecorder.onerror = async (event) => {
        const mediaError = event.error;
        const error: RecordingError = {
          type: "media",
          message: mediaError.message || "Unknown media error",
          retryable: false,
          timestamp: new Date(),
          context: { name: mediaError.name },
        };
        await this.errorHandler.handleError(error);
        this.onStatusChange("failed");
      };

      // Important: avoid timesliced WebM chunks; they frequently lead to files that show 0:00 duration.
      // Record as a single segment and upload once on stop.
      this.mediaRecorder.start();
      this.onStatusChange("recording");
      return true;
    } catch (error) {
      const recordingError: RecordingError = {
        type: "media",
        message: error instanceof Error ? error.message : String(error),
        retryable: false,
        timestamp: new Date(),
      };
      await this.errorHandler.handleError(recordingError);
      this.onStatusChange("failed");
      return false;
    }
  }

  async stop(): Promise<void> {
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
      this.mediaRecorder.stream.getTracks().forEach((track) => track.stop());
    }
  }

  async pause(): Promise<void> {
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      try {
        this.mediaRecorder.pause();
        this.onStatusChange("paused");

        if (this.recordingId) {
          const response = await fetch(
            `/api/sessions/${this.sessionId}/recordings/${this.recordingId}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                status: "paused",
                metadata: {
                  transcriptionState: this.transcriptionState,
                },
              }),
            }
          );

          if (!response.ok) {
            throw new Error("Failed to update recording status");
          }
        }
      } catch (error) {
        const recordingError: RecordingError = {
          type: "database",
          message: error instanceof Error ? error.message : String(error),
          retryable: true,
          timestamp: new Date(),
        };
        await this.errorHandler.handleError(recordingError);
      }
    }
  }

  async resume(): Promise<void> {
    if (this.mediaRecorder && this.mediaRecorder.state === "paused") {
      try {
        this.mediaRecorder.resume();
        this.onStatusChange("recording");

        if (this.recordingId) {
          const response = await fetch(
            `/api/sessions/${this.sessionId}/recordings/${this.recordingId}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                status: "recording",
                metadata: {
                  transcriptionState: this.transcriptionState,
                },
              }),
            }
          );

          if (!response.ok) {
            throw new Error("Failed to update recording status");
          }
        }
      } catch (error) {
        const recordingError: RecordingError = {
          type: "database",
          message: error instanceof Error ? error.message : String(error),
          retryable: true,
          timestamp: new Date(),
        };
        await this.errorHandler.handleError(recordingError);
      }
    }
  }

  updateTranscriptionState(state: SessionState): void {
    this.transcriptionState = state;
  }

  isRecording(): boolean {
    return this.mediaRecorder?.state === "recording";
  }

  isPaused(): boolean {
    return this.mediaRecorder?.state === "paused";
  }

  dispose(): void {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    if (this.recoveryInterval) {
      clearInterval(this.recoveryInterval);
    }
    this.stop();
  }
}
